import type { TeamOfDayView } from '@obraplan/core/application/contracts/team-measurement';
import type { DailyReport } from '@obraplan/core/domain/planning/daily-report';
import type { CrewLine } from '@obraplan/core/domain/planning/daily-report-crew';
import type { DailyReportId, ProjectId } from '@obraplan/core/domain/shared/ids';
import { equipeNaFila, RDO_NA_FILA, type OutboxEntry } from '@/app/outbox-types';
import { syncDoCartao, type SyncDoCartao } from './fila-da-conferencia';

/**
 * A medição por equipe e o RDO com a fila do aparelho por cima (Fase 43, etapa 3, RN-611,
 * RN-612). Como no cartão da pessoa (`fila-da-conferencia.ts`): o efeito esperado de cada
 * operação, até a leitura do servidor chegar com o de verdade.
 */

/** A medição da equipe que ainda não tem id no servidor. A foto dela espera a medição subir. */
export const PENDING_TEAM_MEASUREMENT_ID = 'aparelho:medicao-da-equipe-pendente';

/** O documento do dia que ainda não existe no servidor: o primeiro gesto o cria. */
export const PENDING_REPORT_ID = 'aparelho:rdo-pendente';

/** As equipes do dia com as medições da fila aplicadas. */
export function equipesComAFila(
  teams: readonly TeamOfDayView[],
  entries: readonly OutboxEntry[],
): TeamOfDayView[] {
  return teams.map((team) => ({
    ...team,
    fronts: team.fronts.map((front) => {
      let atual = front;
      for (const { operation, createdAt } of entries) {
        if (
          operation.kind !== 'medir-equipe' ||
          operation.teamId !== team.teamId ||
          operation.activityId !== front.activityId ||
          operation.serviceId !== front.serviceId
        ) {
          continue;
        }
        const { measurement: anterior, ...semMedida } = atual;
        atual =
          operation.quantity === null
            ? semMedida
            : {
                ...semMedida,
                measurement: {
                  id: anterior?.id ?? PENDING_TEAM_MEASUREMENT_ID,
                  quantity: operation.quantity,
                  measuredAt: createdAt,
                },
              };
      }
      return atual;
    }),
  }));
}

/** A marca da frente da equipe. */
export function syncDaFrente(
  entries: readonly OutboxEntry[],
  teamId: string,
  activityId: string,
  serviceId: string,
): SyncDoCartao | undefined {
  const chave = equipeNaFila(teamId, activityId, serviceId);
  return syncDoCartao(entries.filter((entry) => entry.operation.resourceId === chave));
}

/** As operações do RDO, na ordem em que foram feitas. */
export function filaDoRdo(entries: readonly OutboxEntry[]): OutboxEntry[] {
  return entries.filter((entry) => entry.operation.resourceId === RDO_NA_FILA);
}

/**
 * O documento do dia com o que a fila vai gravar nele.
 *
 * Sem documento no servidor e com algo na fila, nasce um rascunho no aparelho: é o que o primeiro
 * gesto faria lá, e a tela mostra as seções já preenchidas.
 */
export function documentoComAFila(
  report: DailyReport | undefined,
  entries: readonly OutboxEntry[],
  dia: { readonly projectId: string; readonly date: string },
): DailyReport | undefined {
  const doRdo = filaDoRdo(entries);
  if (doRdo.length === 0) return report;
  let atual: DailyReport = report ?? {
    id: PENDING_REPORT_ID as DailyReportId,
    projectId: dia.projectId as ProjectId,
    date: dia.date,
    status: 'DRAFT',
    revision: 0,
    shifts: [],
    tasks: [],
    crew: [],
    equipment: [],
    version: 0,
    createdAt: '',
    updatedAt: '',
  };
  for (const { operation } of doRdo) {
    switch (operation.kind) {
      case 'rdo-clima':
        atual = { ...atual, shifts: operation.shifts };
        break;
      case 'rdo-tarefas':
        atual = { ...atual, tasks: operation.tasks };
        break;
      case 'rdo-ocorrencias': {
        const { notes: _antes, ...semNotas } = atual;
        atual = operation.notes.trim() === '' ? semNotas : { ...semNotas, notes: operation.notes };
        break;
      }
      case 'rdo-efetivo':
        atual = { ...atual, crew: operation.crew };
        break;
      case 'rdo-equipamento':
        atual = { ...atual, equipment: operation.equipment };
        break;
      case 'rdo-fotos':
      case 'remover-foto':
        // A foto vai para a galeria, e não para o documento.
        break;
      // Emitida sem sinal (RN-624): o documento fecha na hora — nada mais se edita nele —, e o
      // número é o que já tinha, ou nenhum: quem dá o primeiro é o servidor, quando subir.
      case 'rdo-emitir': {
        if (atual.status === 'CLOSED') break;
        const { outdatedAt: _desatualizado, ...semAviso } = atual;
        atual = { ...semAviso, status: 'CLOSED', revision: atual.revision + 1 };
        break;
      }
      // A revisão aberta sem sinal (RN-625): o documento volta a aceitar alteração, com o número.
      case 'rdo-revisar':
        if (atual.status === 'CLOSED') atual = { ...atual, status: 'OUTDATED' };
        break;
      case 'resolver-nc':
      case 'medir':
      case 'ausencia':
      case 'reabrir':
      case 'fotos-da-conferencia':
      case 'fotos-da-nc':
      case 'medir-equipe':
      case 'fotos-da-equipe':
      case 'comodos':
      case 'pecas-da-frente':
      case 'lancar-trabalho':
      case 'lancar-trabalho-equipe':
      case 'tirar-frente':
        // Não são do RDO: a chave da fila delas é outra.
        break;
    }
  }
  return atual;
}

/**
 * A emissão do RDO que espera na fila (RN-624): a última mudança de estado do documento feita no
 * aparelho foi emitir. O número e o PDF saem quando ela subir; até lá o documento está fechado,
 * mas sem número para pôr no papel.
 */
export function emissaoPendente(entries: readonly OutboxEntry[]): boolean {
  const ultima = filaDoRdo(entries)
    .map((entry) => entry.operation.kind)
    .filter((kind) => kind === 'rdo-emitir' || kind === 'rdo-revisar')
    .at(-1);
  return ultima === 'rdo-emitir';
}

/**
 * O efetivo do papel com a correção da fila: o que quem relatou disse vale por cima do contado, e
 * a função que ficou fora da correção volta ao contado — como o servidor faz ao gravar a lista.
 */
export function efetivoComAFila(
  lines: readonly CrewLine[],
  entries: readonly OutboxEntry[],
): CrewLine[] {
  const ultima = [...filaDoRdo(entries)]
    .reverse()
    .find((entry) => entry.operation.kind === 'rdo-efetivo')?.operation;
  if (ultima === undefined || ultima.kind !== 'rdo-efetivo') return [...lines];
  const relatado = new Map(ultima.crew.map((linha) => [linha.roleName, linha.quantity]));
  const existentes = lines.map((linha) => {
    const quantidade = relatado.get(linha.roleName);
    const { reported: _antes, ...semRelato } = linha;
    return quantidade === undefined
      ? { ...semRelato, quantity: linha.counted }
      : { ...semRelato, reported: quantidade, quantity: quantidade };
  });
  const novas = ultima.crew
    .filter((linha) => !lines.some((existente) => existente.roleName === linha.roleName))
    .map((linha) => ({
      roleName: linha.roleName,
      counted: 0,
      reported: linha.quantity,
      quantity: linha.quantity,
    }));
  return [...existentes, ...novas];
}
